import { BREAKPOINTS } from "../../constants/breakpoints";
import { CSS } from "../../constants/css";
import { debounce } from "../../utils/debounce";
import { getChildIndex } from "../../utils/dom";
import { aosRefreshEvent } from "../aos";
import { userPreferences } from "../../utils/userPreferences";

const handleVideo = (panel: HTMLElement, autoPlay = true) => {
  const video = panel.querySelector<HTMLIFrameElement>(
    ".js-accordion-panel-video",
  );
  if (!video) {
    return;
  }

  const src = video.getAttribute("data-src");
  if (!src) {
    return;
  }

  video.src = autoPlay && !userPreferences.prefersReducedMotion ? src : src.replace("autoplay=1", "autoplay=0");
};

const createAccordion = ($accordion: HTMLElement) => {
  const $panels = $accordion.querySelectorAll<HTMLElement>(
    ".js-accordion-panel",
  );
  if (!$panels.length) return;

  let activeTransition: number | null = null;

  const cleanupTransition = () => {
    if (activeTransition) {
      cancelAnimationFrame(activeTransition);
      activeTransition = null;
    }
    $accordion.classList.remove(CSS.transitioning);
  };

  const openPanel = ($panel: HTMLElement) => {
    // If clicked panel is already open, don't do anything
    if ($panel.classList.contains(CSS.open)) {
      return;
    }

    cleanupTransition();
    $accordion.classList.add(CSS.transitioning);

    // Reset all panels
    $panels.forEach(($p) => {
      $p.classList.remove(CSS.open);
      ["aria-selected", "aria-expanded"].forEach(attr =>
        $p.setAttribute(attr, "false"),
      );
    });

    // Configure target panel
    handleVideo($panel);
    $panel.classList.add(CSS.open);
    ["aria-selected", "aria-expanded"].forEach(attr =>
      $panel.setAttribute(attr, "true"),
    );

    // Cleanup after transition
    const cleanup = () => {
      $panel.removeEventListener("transitionend", cleanup);
      cleanupTransition();

      // Unload videos in closed panels
      $panels.forEach(($p) => {
        if (!$p.classList.contains(CSS.open)) {
          handleVideo($p, false);
        }
      });
    };

    $panel.addEventListener("transitionend", cleanup);
  };

  const handleKeyboard = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (!target.matches("[role=\"tab\"]")) return;

    const currentIdx = getChildIndex(target);
    let nextTab: HTMLElement | undefined;

    const keyActions: Record<string, () => HTMLElement | undefined> = {
      "ArrowLeft": () => $panels[currentIdx - 1] || $panels[$panels.length - 1],
      "ArrowUp": () => $panels[currentIdx - 1] || $panels[$panels.length - 1],
      "ArrowRight": () => $panels[currentIdx + 1] || $panels[0],
      "ArrowDown": () => $panels[currentIdx + 1] || $panels[0],
      "Home": () => $panels[0],
      "End": () => $panels[$panels.length - 1],
      "Enter": () => {
        openPanel(target);
        return undefined;
      },
      " ": () => {
        openPanel(target);
        return undefined;
      },
    };

    if (keyActions[e.key]) {
      e.preventDefault();
      nextTab = keyActions[e.key]();
      if (nextTab) nextTab.focus();
    }
  };

  // Initialize event listeners
  $panels.forEach(($panel) => {
    $panel.addEventListener("click", () => openPanel($panel));
  });
  $accordion.addEventListener("keydown", handleKeyboard);
};

// Accordions have a minimum height. However, some panels may contain taller content,
// so we need to adjust the height of all of the panels to match the tallest content.
// We can't use CSS to handle this because the content in closed panels is squeezed
// and so becomes very tall, so we need to set the width of the content to how it
// would be in its open state to then calculate its height.
// We also need to set minimum dimensions on the background image and videos such that
// when the panels are opened/closed they don't do weird zooming as a result of the
// container aspect ratio changing. The minimum dimensions are set to the largest open
// content element.
const updateSizes = ($accordions: NodeListOf<HTMLElement>) => {
  const isDesktop = window.matchMedia(`(min-width: ${BREAKPOINTS.desktop})`);

  $accordions.forEach(($accordion) => {
    const $panels = $accordion.querySelectorAll<HTMLElement>(
      ".js-accordion-panel",
    );

    // If we're in vertical mode, i.e. < desktop or is-vertical, reset sizing and
    // adjust background image/video so that its closed state is the same size as
    // its open state.
    if (!isDesktop.matches || $accordion.classList.contains("is-vertical")) {
      // Reset content sizing
      const $contents = $accordion.querySelectorAll<HTMLElement>(
        ".js-accordion-panel-content",
      );
      $contents.forEach(($c) => {
        $c.style.height = "auto";
        $c.style.width = "auto";
      });

      // Loop through each panel and set image/video dimensions to open-state size.
      $panels.forEach(($panel) => {
        const isInitiallyOpen = $panel.classList.contains(CSS.open);

        // Temporarily open the panel to measure its dimensions
        if (!isInitiallyOpen) {
          $panel.classList.add(CSS.open);
        }

        const $content = $panel?.querySelector<HTMLElement>(
          ".js-accordion-panel-content",
        );
        const panelContentHeight = $content?.offsetHeight;

        $accordion.style.setProperty(
          "--accordionBlockContentHeight",
          `${panelContentHeight}px`,
        );
        $accordion.style.setProperty("--accordionBlockContentWidth", "auto");

        // Revert the panel to its initial state
        if (!isInitiallyOpen) {
          $panel.classList.remove(CSS.open);
        }
      });

      window.dispatchEvent(new Event(aosRefreshEvent));

      return;
    }

    // If >= desktop and vertical, don't do anything
    if (isDesktop.matches && $accordion.classList.contains("is-vertical")) {
      return;
    }

    // Otherwise we're now dealing with a desktop, horizontal accordion.
    let greatestHeight = 0;
    let contentWidth = 0;

    // Get largest dimensions of all panel contents, i.e in open mode.
    $panels.forEach(($panel) => {
      const $openPanel = [...$panels].find(p =>
        p.classList.contains(CSS.open),
      );
      const $openContent = $openPanel?.querySelector<HTMLElement>(
        ".js-accordion-panel-content",
      );
      const isInitiallyOpen = $panel.classList.contains(CSS.open);

      contentWidth = $openContent?.offsetWidth ?? 0;

      // Temporarily open the panel to measure its dimensions
      if (!isInitiallyOpen) {
        $panel.classList.add(CSS.open);
      }

      const $content = $panel.querySelector<HTMLElement>(
        ".js-accordion-panel-content",
      );
      if ($content) {
        const initialHeight = $content.style.height;
        $content.style.height = "auto";
        $content.style.width = `${contentWidth}px`;
        const contentHeight = $content.offsetHeight;

        // Update the maximum dimensions
        if (contentHeight > greatestHeight) {
          greatestHeight = contentHeight;
        }

        $content.style.height = initialHeight;
      }

      // Revert the panel to its initial state
      if (!isInitiallyOpen) {
        $panel.classList.remove(CSS.open);
      }
    });

    $accordion.style.setProperty(
      "--accordionBlockContentHeight",
      `${greatestHeight}px`,
    );
    $accordion.style.setProperty(
      "--accordionBlockContentWidth",
      `${contentWidth}px`,
    );

    window.dispatchEvent(new Event(aosRefreshEvent));
  });
};

export default () => {
  const $accordions = document.querySelectorAll<HTMLElement>(".js-accordion");
  $accordions.forEach(createAccordion);

  const debouncedSizeCheck = debounce(() => updateSizes($accordions), 250);
  ["resize", "orientationchange"].forEach(event =>
    window.addEventListener(event, debouncedSizeCheck),
  );

  updateSizes($accordions);
};
